
import { Vue, Options } from "vue-class-component";
import { Emit, Model, Prop, Watch } from "vue-property-decorator";
import { ElMessage, ElMessageBox } from "element-plus";
import MiezzButton from "@/models/MiezzButton";
import MiezzForm, { EnumMiezzFormItemType } from "@/models/MiezzForm";
import CompanyAppService from "@/apis.machine/CompanyAppService";
import DtoDetailCompany from "@/models.machine/DtoDetailCompany";
import { EnumCompanyType } from "@/models.machine/EnumCompanyType";
import { CompanySetting } from "./CompanySetting";
import CurrentLogier from "@/apis/CurrentLogier";
import { MiezzMenuTab } from "@/components/MiezzMenuTab.vue";
import {
  EnumBusinessDate,
  EnumBusinessDateOption,
} from "@/models.machine/EnumBusinessDate";
import MiezzModal, { MiezzModalType } from "@/models/MiezzModal";
import { h } from "vue";
import ParamCurrencySystemAppService from "@/apis.machine/ParamCurrencySystemAppService";
import UiSelectOption from "@/models.machine/UiSelectOption";

export default class CompanyDetail extends Vue {
  @Prop() menuTab?: MiezzMenuTab;
  @Prop() type?: EnumCompanyType;
  @Prop() detail!: MiezzForm<DtoDetailCompany, string>;
  @Prop() buttons: MiezzButton[] = [];
  @Prop() onGet?: (model: DtoDetailCompany) => void;
  @Prop() settings: CompanySetting[] = [];
  @Prop() nameEnAbbr?: boolean;
  @Prop() canEditBusinessDate?: boolean;
  @Prop() canEditCurrencySystem?: boolean;

  id?: string;
  tab = "BasicInfo";

  EnumBusinessDateOption = EnumBusinessDateOption;

  modalBusinessDate = new MiezzModal(MiezzModalType.Dialog);
  formBusinessDate = new MiezzForm<
    {
      BusinessDate?: EnumBusinessDate;
    },
    string
  >();

  modalCurrencySystem = new MiezzModal(MiezzModalType.Dialog);
  formCurrencySystem = new MiezzForm<
    {
      CurrencySystemId?: string;
      ShowTaxRate?: boolean;
    },
    string
  >();
  currencySystems: UiSelectOption<string>[] = [];

  /**创建时 */
  created(): void {
    this.tab = this.$route.query.page as string;
    const logier = CurrentLogier.GetLogier();
    this.id = this.$route.params.id as string;
    if (!this.id) this.id = logier?.CompanyId;
    this.detail.DefaultSpan = 8;
    this.detail.Data = new DtoDetailCompany();

    this.$nextTick(() => {
      for (const item of this.detail.Items ?? []) {
        switch (item.Prop) {
          case "NameEn":
            item.HandleFormat = () => {
              return this.nameEnAbbr
                ? `${this.detail.Data.NameEn ?? "暂无数据"}/${this.detail.Data.NameEnAbbr ?? "暂无数据"
                }`
                : this.detail.Data.NameEn ?? "暂无数据";
            };
            break;
          case "ServiceProviderCompanyId":
            item.HandleFormat = () =>
              this.detail.Data.ServiceProviderCompanyId
                ? `${this.detail.Data.ServiceProviderCompanyNameZh ?? "暂无数据"
                }/${this.detail.Data.ServiceProviderCompanyNameEn ?? "暂无数据"
                }`
                : "暂无数据";
            break;
          case "ParamCompanyCodeType":
            item.HandleFormat = () => {
              const data = this.detail.Data.ParamCompanyCodeType;
              return data?.ParamCountry
                ? `${data?.ParamCountry?.NameZh ?? "暂无数据"}/${data?.ParamCountry?.NameEn ?? "暂无数据"
                }/${data?.CountryCode ?? "暂无数据"}/${data?.CompanyCode ?? "暂无数据"
                }`
                : undefined;
            };
            break;
          case "ParamCountry":
            item.HandleFormat = () => {
              const data = this.detail.Data.ParamCountry;
              return data
                ? `${data?.NameZh ?? "暂无数据"}/${data?.NameEn ?? "暂无数据"}`
                : undefined;
            };
            break;
          case "ParamTaxType":
            item.HandleFormat = () => {
              const data = this.detail.Data.ParamTaxType;
              return data ? data.FullName ?? "暂无数据" : undefined;
            };
            break;
          case "CompanyInvoiceTaxRates":
            item.HandleFormat = () => {
              return this.detail.Data.CompanyInvoiceTaxRates?.map(
                (it) => `【${it.ParamInvoiceTaxRate?.FullName ?? "暂无数据"}】`
              ).join("");
            };
            break;
          case "JoinCompanyTypes":
            item.HandleFormat = () => {
              return this.detail.Data.JoinCompanyTypes?.map(
                (it) => `【${it.CompanyType?.Name}】`
              ).join("");
            };
            break;
          default:
            break;
        }
      }
    });

    CompanyAppService.Get(this.id).then((r) => {
      if (r.data.Data) {
        this.detail.Data = r.data.Data;
        if (this.onGet) {
          this.onGet(this.detail.Data);
        }
      }
    });

    this.modalBusinessDate.Title = "业务周期";
    this.modalBusinessDate.Height = "100px";
    this.modalBusinessDate.Buttons = [
      {
        Code: "save-business-date",
        Label: "保存",
        Type: "primary",
        Size: "small",
      },
    ];
    this.modalBusinessDate.HandleClick = this.handleClick;
    this.formBusinessDate.Items = [
      {
        Prop: "BusinessDate",
        Label: "业务周期",
        Customize: true,
      },
    ];

    this.modalCurrencySystem.Title = "账单参数设置";
    this.modalCurrencySystem.Height = "100px";
    this.modalCurrencySystem.Buttons = [
      {
        Code: "save-currency-system",
        Label: "保存",
        Type: "primary",
        Size: "small",
      },
    ];
    this.modalCurrencySystem.HandleClick = this.handleClick;
    this.formCurrencySystem.Items = [
      {
        Prop: "CurrencySystemId",
        Label: "币制",
        Customize: true,
      },
      {
        Prop: "ShowTaxRate",
        Label: "显示税率",
        Type: EnumMiezzFormItemType.Switch,
      },
    ];
    ParamCurrencySystemAppService.GetSelect().then(r => {
      this.currencySystems = r.data.Data ?? []
    })
  }

  handleLinkAfter(route: any): void {
    this.tab = route.query.page as string;
  }

  saveBusinessDate(complete: () => void): void {
    MiezzForm.submit(this.$refs, "ref-form-business-date", (v) => {
      if (v) {
        CompanyAppService.SetBusinessDate(this.formBusinessDate.Data)
          .then(() => {
            this.detail.Data.BusinessDate =
              this.formBusinessDate.Data.BusinessDate;
            this.modalBusinessDate.Show = false;
            ElMessage.success("编辑成功");
            complete();
          })
          .catch(complete);
      } else {
        complete();
      }
    });
  }

  /**按钮点击 */
  handleClick(btn: MiezzButton, complete: () => void): void {
    const listUrl = this.$route.path.substring(
      0,
      this.$route.path.indexOf("/detail")
    );
    switch (btn.Code) {
      case "save-business-date":
        if (
          this.formBusinessDate.Data.BusinessDate != undefined &&
          this.formBusinessDate.Data.BusinessDate !=
          this.detail.Data.BusinessDate
        ) {
          if (
            this.formBusinessDate.Data.BusinessDate == EnumBusinessDate.Default
          ) {
            ElMessageBox({
              title: "提示",
              message:
                "保存后所有订单的业务周期按照系统默认规则（ATD＞ETD＞ATA＞ETA）自动生成，并可由用户手动调整，是否继续当前操作？",
              confirmButtonText: "是",
              cancelButtonText: "否",
            })
              .then(() => {
                this.saveBusinessDate(complete);
              })
              .catch(() => {
                complete();
              });
          } else {
            const label = EnumBusinessDateOption.firstOrDefault(
              (it) => it.Value == this.formBusinessDate.Data.BusinessDate
            )?.Label;
            ElMessageBox({
              title: "提示",
              message: h("p", null, [
                h("span", null, "保存后所有订单（"),
                h(
                  "span",
                  {
                    style: "color:red",
                  },
                  label + "不为空"
                ),
                h(
                  "span",
                  null,
                  `）的业务周期将跟${label}所在月份保持一致，包含已锁定的订单，是否继续当前操作？ `
                ),
              ]),
              confirmButtonText: "是",
              cancelButtonText: "否",
            })
              .then(() => {
                this.saveBusinessDate(complete);
              })
              .catch(() => {
                complete();
              });
          }
        } else {
          this.saveBusinessDate(complete);
        }
        break;
      case "save-currency-system":
        MiezzForm.submit(this.$refs, "ref-form-currency-system", (v) => {
          if (v) {
            CompanyAppService.SetCurrencySystem(this.formCurrencySystem.Data)
              .then(() => {
                this.detail.Data.CurrencySystemId =
                  this.formCurrencySystem.Data.CurrencySystemId;
                this.detail.Data.ShowTaxRate =
                  this.formCurrencySystem.Data.ShowTaxRate;
                this.modalCurrencySystem.Show = false;
                ElMessage.success("编辑成功");
                complete();
              })
              .catch(complete);
          } else {
            complete();
          }
        });
        break;
      case "edit":
        this.$router.push(`${listUrl}/edit/${this.id}`);
        complete();
        break;
      case "delete":
        ElMessageBox.confirm("是否确认将该信息删除？", "提醒", {
          confirmButtonText: "确认",
          cancelButtonText: "取消",
          type: "warning",
        })
          .then(() => {
            CompanyAppService.Delete([this.detail.Data.Id as string])
              .then(() => {
                this.$router.push(listUrl);
                complete();
              })
              .catch(complete);
          })
          .catch(() => {
            complete();
          });
        break;
      case "back":
        CurrentLogier.Back();
        complete();
        break;
      default:
        break;
    }
  }

  @Emit()
  handleRowDbClick(row: any): void {
    if (row.Url) {
      const url = row.Url.replace("${id}", this.id ?? "");
      this.$router.push(url);
    }
  }

  handleSettingClick(
    btn: MiezzButton,
    row: CompanySetting,
    complete: () => void
  ): void {
    if (btn.Code == "link" && row.Url) {
      if (row.Url == "BusinessDate") {
        this.formBusinessDate.Data = {
          BusinessDate:
            this.detail.Data.BusinessDate ?? EnumBusinessDate.Default,
        };
        this.modalBusinessDate.Show = true;
      } else if (row.Url == "CurrencySystem") {
        this.formCurrencySystem.Data = {
          CurrencySystemId:
            this.detail.Data.CurrencySystemId,
          ShowTaxRate: this.detail.Data.ShowTaxRate
        };
        this.modalCurrencySystem.Show = true;
      } else {
        const url = row.Url.replace("${id}", this.id ?? "");
        this.$router.push(url);
      }
    }
    complete();
  }
}
